import React from 'react'
import { usePrivacyPolicy, PrivacyPolicyProps } from './usePrivacyPolicy'
import styles from './PrivacyPolicy.module.scss'
import InnerTitleSection from 'components/InnerTitleSection/InnerTitleSection'

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = (
  props: PrivacyPolicyProps
) => {
  const {} = usePrivacyPolicy(props)

  return (
    <main id='privacy-policy' className={styles['privacy-policy']}>
      <InnerTitleSection
        title='Privacy Policy'
        breadcrumbs={[
          {
            title: 'Home',
            link: '/',
          },
          {
            title: 'Privacy Policy',
          },
        ]}
      />
      <div className='container py-5'>
        <div className='row'>
          <div className='col-12 mb-4'>
            <h4>Effective Date: 1 July 2025</h4>
            <p>
              <b>ByteBuffetBox</b> (“we,” “our,” or “us”) is committed to
              protecting your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you
              use our website and services. <br />
              By accessing or using <b>ByteBuffetBox</b>, you consent to the
              practices described in this Privacy Policy.
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>1. Information We Collect</h4>
            <p>We may collect the following types of information:</p>
            <h5>a. Personal Information</h5>
            <p>When you register or contact us, we may collect:</p>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Business name</li>
              <li>Location</li>
              <li>Payment information</li>
            </ul>
            <h5>b. Usage Data</h5>
            <p>We may collect data about how you use our platform, such as:</p>
            <ul>
              <li>Browser type and version</li>
              <li>Pages visited</li>
              <li>Time spent on pages</li>
              <li>Device information</li>
              <li>IP address</li>
            </ul>
            <h5>c. Menu and Restaurant Data</h5>
            <p>
              As part of the service, you may upload menu content, images,
              categories, prices, and other business-related data. This data is
              stored securely and used solely to provide the digital menu
              service.
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>2. How We Use Your Information</h4>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide and manage your account.</li>
              <li>Deliver and improve our services.</li>
              <li>Personalize user experience.</li>
              <li>
                Communicate with you regarding your account or support inquiries.
              </li>
              <li>
                Send important updates or promotional emails (you can opt out
                anytime).
              </li>
              <li>Monitor and analyze platform performance and usage.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </div>
          <div className='col-12 mb-4'>
            <h4>3. How We Share Your Information</h4>
            <p>
              We do not sell, rent, or share your personal data with third
              parties for marketing purposes.
              <br /> We may share data only with:
            </p>
            <ul>
              <li>
                Service providers (e.g. payment processors, hosting providers)
                under confidentiality agreements.
              </li>
              <li>
                Legal authorities, if required by law, regulation, or legal
                process.
              </li>
              <li>
                In the event of a merger or acquisition, with the acquiring
                entity under privacy protections.
              </li>
            </ul>
          </div>
          <div className='col-12 mb-4'>
            <h4>4. Data Security</h4>
            <p>
              We implement industry-standard security measures to protect your
              personal data, including encryption, firewalls, and secure access
              controls. However, no system is completely secure, and we cannot
              guarantee absolute security.
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>5. Your Data Rights</h4>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>Access or request a copy of your personal data.</li>
              <li>Correct inaccurate data.</li>
              <li>Delete your account and associated data.</li>
              <li>Withdraw consent for marketing communications.</li>
              <li>Lodge a complaint with a data protection authority.</li>
            </ul>
            <p>
              You can exercise these rights by contacting us at: <br />{' '}
              <i className='bi bi-envelope-open me-1'></i>{' '}
              <a href='mailto:support@bytebuffetbox.com'>
                support@bytebuffetbox.com
              </a>
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>6. Data Retention</h4>
            <p>
              We retain your information as long as necessary to provide
              services and fulfill legal obligations. If you delete your
              account, we will remove or anonymize your personal data unless
              retention is legally required.
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>7. Cookies</h4>
            <p>
              We may use cookies and similar technologies to enhance site
              performance and user experience. You can manage your cookie
              preferences through your browser settings.
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>8. Third-Party Links</h4>
            <p>
              Our platform may contain links to external websites. We are not
              responsible for the content or privacy practices of those
              websites. We encourage you to read their policies.
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>9. Changes to This Policy</h4>
            <p>
              We may update this Privacy Policy occasionally. Any changes will
              be posted on this page with a new effective date. We encourage you
              to review it periodically.
            </p>
          </div>
          <div className='col-12 mb-4'>
            <h4>10. Contact Us</h4>
            <p>
              If you have questions about this Privacy Policy or how we handle
              your data, contact us at:
              <br />
              <i className='bi bi-envelope-open me-1'></i>{' '}
              <a href='mailto:support@bytebuffetbox.com'>
                support@bytebuffetbox.com
              </a>{' '}
              <br />
              <i className='bi bi-telephone-fill me-1'></i>{' '}
              <a href='tel:+96176851367'>+961 76 951 367</a>
            </p>
          </div>
        </div>
      </div>
    </main>
  )
}

export default PrivacyPolicy
